import { useEffect, useState } from "react";
import { Scale, Plus, Trash2, Save, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/* Admin: edit the airline fare-attributes database so /fares stays current. Moderators only. */
const STATUSES = [["yes", "Included"], ["fee", "For a fee"], ["no", "Not included"], ["part", "Limited/varies"]];
const inp = "border border-zinc-300 px-2 py-1.5 text-xs w-full bg-white outline-none focus:border-indigo-500";
const emptyFare = () => ({ name: "New fare", note: null, attrs: {} });

export default function AdminFares() {
  const [list, setList] = useState([]);
  const [attrs, setAttrs] = useState([]);
  const [sel, setSel] = useState(null); // airline doc being edited
  const [busy, setBusy] = useState(false);

  const loadList = () => api.get("/fares/airlines").then((r) => { setList(r.data.items); setAttrs(r.data.attributes); });
  useEffect(() => {
    // right after login the first fetch can race the auth/token bootstrap — retry once
    loadList().catch(() => setTimeout(() => loadList().catch(() => toast.error("Could not load fare data — reload the page")), 1200));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (list.length === 0) { const t = setTimeout(() => loadList().catch(() => {}), 1500); return () => clearTimeout(t); }
  }, [list.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (iata) => {
    const { data } = await api.get(`/fares/${iata}`);
    setSel({ iata: data.iata, name: data.name, region: data.region || "", fares: data.fares });
  };

  const newAirline = () => {
    const iata = window.prompt("New airline IATA code (e.g. LX):");
    if (!iata || !/^[A-Za-z0-9]{2,3}$/.test(iata.trim())) return;
    setSel({ iata: iata.trim().toUpperCase(), name: "", region: "", fares: [emptyFare()] });
  };

  const setFare = (fi, patch) => setSel((s) => ({ ...s, fares: s.fares.map((f, i) => (i === fi ? { ...f, ...patch } : f)) }));
  const setCell = (fi, key, patch) => setSel((s) => ({ ...s, fares: s.fares.map((f, i) => i !== fi ? f
    : { ...f, attrs: { ...f.attrs, [key]: { s: "part", t: "", ...(f.attrs[key] || {}), ...patch } } }) }));

  const save = async () => {
    if (!sel.name.trim()) return toast.error("Airline name required");
    setBusy(true);
    try {
      await api.put(`/fares/admin/${sel.iata}`, sel);
      toast.success(`${sel.name} saved — live on /fares now`);
      loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setBusy(false);
  };

  const remove = async () => {
    if (!window.confirm(`Remove ${sel.iata} from the fare database?`)) return;
    try { await api.delete(`/fares/admin/${sel.iata}`); toast.success("Removed"); setSel(null); loadList(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-zinc-50 py-10">
      <div className="max-w-7xl mx-auto px-4">
        <h1 className="font-heading font-900 text-3xl flex items-center gap-2.5 mb-1">
          <Scale className="w-8 h-8 text-indigo-600" /> Fare Database Admin
        </h1>
        <p className="text-sm text-muted-foreground mb-6">Edit what each fare class includes — changes go live on the public /fares page immediately.</p>

        <div className="flex flex-wrap gap-1.5 mb-6" data-testid="admin-fares-list">
          {list.map((a) => (
            <button key={a.iata} onClick={() => open(a.iata)} data-testid={`admin-fare-open-${a.iata}`}
              className={`px-3 py-1.5 text-sm font-600 border ${sel?.iata === a.iata ? "border-indigo-600 bg-indigo-600 text-white" : "border-zinc-300 bg-white hover:border-indigo-400"}`}>
              {a.name} <span className="opacity-60 text-xs">({a.iata})</span>
            </button>
          ))}
          <button onClick={newAirline} data-testid="admin-fare-new"
            className="px-3 py-1.5 text-sm font-700 border border-dashed border-zinc-400 text-zinc-600 hover:border-indigo-500 hover:text-indigo-700 inline-flex items-center gap-1">
            <Plus className="w-4 h-4" /> Add airline
          </button>
        </div>

        {sel && (
          <div className="bg-white border border-zinc-200" data-testid="admin-fare-editor">
            <div className="h-1 bg-indigo-600" />
            <div className="p-4 flex flex-wrap items-center gap-2 border-b border-zinc-100">
              <span className="font-mono font-900 text-lg">{sel.iata}</span>
              <input value={sel.name} onChange={(e) => setSel((s) => ({ ...s, name: e.target.value }))}
                placeholder="Airline name" className={`${inp} !text-sm max-w-56`} data-testid="admin-fare-name" />
              <input value={sel.region} onChange={(e) => setSel((s) => ({ ...s, region: e.target.value }))}
                placeholder="Region" className={`${inp} !text-sm max-w-40`} data-testid="admin-fare-region" />
              <div className="ml-auto flex gap-2">
                <button onClick={() => setSel((s) => ({ ...s, fares: [...s.fares, emptyFare()] }))} data-testid="admin-fare-add-tier"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-700 border border-zinc-300 hover:border-indigo-500"><Plus className="w-3.5 h-3.5" /> Fare tier</button>
                <button onClick={remove} data-testid="admin-fare-delete"
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-700 border border-rose-300 text-rose-700 hover:bg-rose-50"><Trash2 className="w-3.5 h-3.5" /> Remove airline</button>
                <button onClick={save} disabled={busy} data-testid="admin-fare-save"
                  className="inline-flex items-center gap-1 px-4 py-1.5 text-xs font-700 bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
                  {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[840px] border-collapse">
                <thead>
                  <tr className="border-b border-zinc-200">
                    <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest text-muted-foreground w-40">Attribute</th>
                    {sel.fares.map((f, fi) => (
                      <th key={fi} className="px-2 py-2">
                        <div className="flex items-center gap-1">
                          <input value={f.name} onChange={(e) => setFare(fi, { name: e.target.value })}
                            className={`${inp} font-700`} data-testid={`admin-tier-name-${fi}`} />
                          <button onClick={() => setSel((s) => ({ ...s, fares: s.fares.filter((_, i) => i !== fi) }))}
                            title="Remove tier" className="text-zinc-300 hover:text-rose-600" data-testid={`admin-tier-remove-${fi}`}><Trash2 className="w-3.5 h-3.5" /></button>
                        </div>
                        <input value={f.note || ""} onChange={(e) => setFare(fi, { note: e.target.value || null })}
                          placeholder="Note (optional)" className={`${inp} mt-1 !text-[10px]`} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {attrs.map((at) => (
                    <tr key={at.key} className="border-b border-zinc-100">
                      <td className="px-3 py-2 text-xs font-600 text-zinc-600">{at.label}</td>
                      {sel.fares.map((f, fi) => {
                        const c = f.attrs[at.key] || { s: "part", t: "" };
                        return (
                          <td key={fi} className="px-2 py-1.5 align-top">
                            <select value={c.s} onChange={(e) => setCell(fi, at.key, { s: e.target.value })}
                              className={`${inp} mb-1`} data-testid={`admin-cell-s-${fi}-${at.key}`}>
                              {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                            <input value={c.t} onChange={(e) => setCell(fi, at.key, { t: e.target.value })}
                              placeholder="Short detail" className={inp} data-testid={`admin-cell-t-${fi}-${at.key}`} />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
